/**
 * @copyright Copyright 2025 Epic Systems Corporation
 * @file Vendor Services Copy Link button
 * @module Epic.AppOrchard.Core.CopyLinkButton
 */

import { LinkIcon } from "@chakra-ui/icons";
import { Button, useClipboard, useToast } from "@chakra-ui/react";
import { config } from "ao/appConfig";
import { FC, useCallback, useEffect } from "react";

interface IProps extends React.ComponentProps<typeof Button> {
	textToCopy: string;
}

export const CopyLinkButton: FC<IProps> = (props: IProps) => {
	const { children, textToCopy, ...rest } = props;
	const { onCopy, hasCopied } = useClipboard(textToCopy);
	const toast = useToast();

	const displayCopyToast = useCallback(() => {
		toast({
			title: "Link Copied.",
			description: "Link copied successfully to your clipboard!",
			status: "success",
			variant: "success",
			duration: config.ToastDuration,
			isClosable: true,
			position: "top-right",
		});
	}, [toast]);

	useEffect(() => {
		if (hasCopied) {
			displayCopyToast();
		}
	}, [displayCopyToast, hasCopied]);

	return (
		<Button
			onClick={onCopy}
			variant="link"
			colorScheme="blue"
			leftIcon={<LinkIcon />}
			margin={1}
			{...rest}
		>
			{children}
		</Button>
	);
};

export default CopyLinkButton;
